/**
 * EmailCheckStatusLine — read-only status of the automatic NeverBounce check (migration 2026-10-08-393).
 *
 * Every flight email is checked before it is sent; nobody triggers it. While a send is waiting on checks this
 * says so ("Checking N addresses — sending starts automatically when done."), and after a send it notes any
 * recipients skipped because their address could not be checked within 24 hours. It renders nothing when
 * there is nothing to say (not started, passed with no skips, or the check is off for this flight). The
 * "Checking …" line requires the RPC's per-flight `enabled`; the skipped-recipients note is history and is
 * shown regardless.
 */
import { useEffect, useState } from "react";
import {
  getFlightEmailCheckStatus,
  type FlightEmailCheckStatus,
} from "../../services/campaigns.service";

interface Props {
  flightId: string;
  /** The flight's status; the line polls only while a send can be waiting. */
  flightStatus: string;
}

const POLL_MS = 15_000;

export default function EmailCheckStatusLine({
  flightId,
  flightStatus,
}: Props) {
  const [data, setData] = useState<FlightEmailCheckStatus | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const live = flightStatus === "sending" || flightStatus === "scheduled";
    const load = async () => {
      try {
        const d = await getFlightEmailCheckStatus(flightId);
        if (!cancelled) {
          setData(d);
        }
      } catch (e) {
        // Advisory only: never block the page on it.
        console.error(
          "[email-check] status failed:",
          e instanceof Error ? e.message : String(e),
        );
      }
      if (!cancelled && live) {
        timer = setTimeout(load, POLL_MS);
      }
    };
    void load();
    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [flightId, flightStatus]);

  if (!data) {
    return null;
  }

  // Only claim an active check while the check is actually on for THIS flight (switch on and the flight in
  // scope). A flight that parked and was then switched off / removed from the allowlist sends unchecked:
  // the server gate answers 'disabled', so "Checking …" would be false (audit a44d5e07 P2).
  const waiting =
    data.enabled === true &&
    flightStatus === "sending" &&
    (data.state === "waiting" || data.state === "scanning");
  const skipped = data.no_result_skipped ?? 0;

  if (!waiting && skipped === 0) {
    return null;
  }

  return (
    <div className="px-6 pb-4 space-y-2" data-testid="email-check-status">
      {waiting && (
        <div
          role="status"
          className="rounded border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-900"
        >
          {data.state === "scanning" || data.waiting_addresses === 0 ? (
            <span className="font-semibold">
              Checking this flight's email addresses — sending starts
              automatically when done.
            </span>
          ) : (
            <span className="font-semibold">
              Checking {data.waiting_addresses.toLocaleString()} address
              {data.waiting_addresses === 1 ? "" : "es"} — sending starts
              automatically when done.
            </span>
          )}{" "}
          Every address is checked with NeverBounce before it is emailed;
          addresses checked in the last year are not checked again.
        </div>
      )}
      {skipped > 0 && (
        <p className="text-sm text-gray-600">
          {skipped.toLocaleString()} recipient
          {skipped === 1 ? " was" : "s were"} skipped because NeverBounce could
          not check {skipped === 1 ? "their address" : "their addresses"} within
          24 hours.
        </p>
      )}
    </div>
  );
}
